import { CATALOG_CATEGORIES } from "@RHCommerceDev/utils/constants";

interface NavigationElement {
  adapter?: Maybe<Scalars["String"]>;
  categoryType?: Maybe<Scalars["String"]>;
  childCategories?: Maybe<Array<NavigationElement>>;
  displayName?: Maybe<Scalars["String"]>;
  displayNameEN?: Maybe<Scalars["String"]>;
  id?: Maybe<Scalars["String"]>;
  isWaterworks?: Maybe<Scalars["Boolean"]>;
  linkType?: Maybe<Scalars["String"]>;
  navImage?: Maybe<Scalars["String"]>;
  siteId?: Maybe<Scalars["String"]>;
  targetUrl?: Maybe<Scalars["String"]>;
  uxAttributes?: Maybe<NavUxAttributes>;
}

// Transformer to convert SaleNavigationElement to NavigationElement shape
function transformSaleToNavigation(
  saleCatalogNavigationArray?: SaleNavigationElement[] | undefined | null
): NavigationElement[] {
  if (!saleCatalogNavigationArray) return [];

  return saleCatalogNavigationArray.map(
    (saleItem): NavigationElement => ({
      ...saleItem,
      displayNameEN: saleItem.displayName ?? "",
      childCategories: transformSaleToNavigation(saleItem?.childCategories)
    })
  );
}

// Hook that returns parsed navigation data
const parsedCatalogNavigation = (
  catalogNavigation: NavigationElement | undefined | null,
  saleCatalogNavigation: SaleNavigationElement | undefined | null,
  isConcierge?: boolean
): NavigationElement | null | undefined => {
  const childCategories = catalogNavigation?.childCategories?.map(
    (category: NavigationElement) => {
      //checks if current category is sale, registry or wishlist
      const isSaleCategory =
        category?.displayNameEN?.toLowerCase() ===
        CATALOG_CATEGORIES.SALE?.toLowerCase();
      const isRegistryCategory =
        category?.displayNameEN?.toLowerCase() ===
        CATALOG_CATEGORIES.REGISTRY?.toLowerCase();

      //for wishlist, it might come with heart symbol, so check if displayNameEN includes "wish list"
      const isWishlistCategory = category?.displayNameEN
        ?.toLowerCase()
        ?.includes(CATALOG_CATEGORIES.WISHLIST.toLowerCase());

      const isCategoryHiddenForConcierge =
        isConcierge && (isRegistryCategory || isWishlistCategory);

      if (isCategoryHiddenForConcierge) {
        return;
      }

      if (isSaleCategory) {
        return {
          ...category,
          childCategories: transformSaleToNavigation(
            saleCatalogNavigation?.childCategories
          )
        };
      }

      return category;
    }
  );

  const updatedNavigation = {
    ...catalogNavigation,
    childCategories: childCategories?.filter(c => c !== undefined)
  };

  return updatedNavigation;
};

export function groupChildCategories(
  childCategories: NavigationElement[] | null | undefined
): { header: NavigationElement | null; children: NavigationElement[] }[] {
  if (!childCategories || childCategories.length === 0) return [];

  const groups: {
    header: NavigationElement | null;
    children: NavigationElement[];
  }[] = [];

  let currentHeader: NavigationElement | null = null;
  let currentGroup: NavigationElement[] = [];

  for (const item of childCategories) {
    if (item.categoryType === "header") {
      // Save previous group
      groups.push({ header: currentHeader, children: currentGroup });
      // Start new group
      currentHeader = item;
      currentGroup = [];
    } else {
      currentGroup.push(item);
    }
  }

  // Push the final group
  groups.push({ header: currentHeader, children: currentGroup });

  return groups.filter(
    group => group.children.length > 0 || group.header != null
  );
}

export default parsedCatalogNavigation;
